import { ReactNode, RefObject, useEffect, useMemo, useState } from "react"
import { CallVideo, useLocalSessionId, useParticipantIds, useScreenShare } from "./callTransport"
import { ChevronLeft, ChevronRight, MonitorUp, Volume2 } from "lucide-react"
import type { CallLayoutMode } from "./useCallLayout"
import { useActiveSpeakerHysteresis } from "./useActiveSpeakerHysteresis"
import { useTileSubscriptionCap } from "./useTileSubscriptionCap"
import { VideoTile } from "./VideoTile"
import { useScreenSharerLabel } from "./useScreenSharerLabel"

export type StageViewMode = "speaker" | "grid"

type StageProps = {
  viewMode: StageViewMode
  layoutMode: CallLayoutMode
  stageVideoRef: RefObject<HTMLVideoElement>
  /** Participant kept on the stage regardless of who is speaking (screen share still wins). */
  pinnedId?: string | null
  onTogglePin?: (sessionId: string) => void
  onActiveSpeakerChange?: (sessionId: string | null) => void
  /**
   * Non-video content that takes priority on the stage (a live poll or Inspire Card).
   * Every camera moves to the thumbnail rail; an ongoing screen share returns when the card closes.
   */
  featured?: ReactNode
  /** Stops the local screen share from the "You're sharing" card that replaces the sharer's own feed. */
  onStopScreenShare?: () => void
}

const SCREEN_AUDIO_STATES = new Set(["playable", "sendable", "loading"])

/** True when a screen share carries a tab or system audio track. */
export const screenShareHasAudio = (screen: { screenAudio?: { state?: string } } | null | undefined) =>
  Boolean(screen?.screenAudio?.state && SCREEN_AUDIO_STATES.has(screen.screenAudio.state))

/**
 * Below this stage width, when the stage is also taller than it is wide, the thumbnail rail
 * moves under the speaker instead of beside them. A 768 px tablet with the tools panel open
 * leaves about 450 px, where a side rail would make the featured speaker narrower than the
 * thumbnails; a short landscape-phone stage keeps the side rail so the speaker keeps its height.
 */
export const STACKED_RAIL_MAX_WIDTH = 700

export const shouldStackRail = (size: { width: number; height: number } | null) =>
  size !== null && size.width < STACKED_RAIL_MAX_WIDTH && size.height >= size.width

export const getMaxGridColumns = (layoutMode: CallLayoutMode) =>
  layoutMode === "desktop" ? 5 : layoutMode === "tablet" ? 4 : layoutMode === "mobile-landscape" ? 3 : 2

/** Column count used before the grid's own size is known (and in environments without ResizeObserver). */
export const getGridColumns = (count: number, layoutMode: CallLayoutMode) => {
  if (count <= 1) return 1
  return Math.min(getMaxGridColumns(layoutMode), Math.ceil(Math.sqrt(count)))
}

export type GridFit = { columns: number; tileWidth: number }

/**
 * Largest 16:9 tiles that fit `count` tiles inside a width × height area with the given gap,
 * trying every column count up to `maxColumns`. Sizing by both dimensions keeps a lone
 * participant's tile (and big grids on short screens) from overflowing the stage, which used
 * to clip the name label at the bottom.
 */
export const fitGridTiles = (count: number, width: number, height: number, maxColumns: number, gap: number): GridFit | null => {
  if (count <= 0 || width <= 0 || height <= 0) return null
  const columnLimit = Math.max(1, Math.min(maxColumns, count))
  let best: GridFit | null = null
  for (let columns = 1; columns <= columnLimit; columns += 1) {
    const rows = Math.ceil(count / columns)
    const cellWidth = (width - gap * (columns - 1)) / columns
    const cellHeight = (height - gap * (rows - 1)) / rows
    const tileWidth = Math.floor(Math.min(cellWidth, cellHeight * (16 / 9)))
    if (tileWidth <= 0) continue
    if (!best || tileWidth > best.tileWidth) best = { columns, tileWidth }
  }
  return best
}

type ContentSize = { width: number; height: number }

const RAIL_GAP = 8
const RAIL_MIN_TILE_HEIGHT = 64
const RAIL_PAGER_HEIGHT = 40

/** Keep thumbnails legible on short screens, paging only when fitting would make them too small. */
const getRailPageSize = (count: number, height?: number) => {
  if (!height) return Math.max(1, count)
  const fullCapacity = Math.floor((height + RAIL_GAP) / (RAIL_MIN_TILE_HEIGHT + RAIL_GAP))
  if (count <= fullCapacity) return Math.max(1, count)
  return Math.max(1, Math.floor((height - RAIL_PAGER_HEIGHT + RAIL_GAP) / (RAIL_MIN_TILE_HEIGHT + RAIL_GAP)))
}

/** Content-box size of an element, or null until ResizeObserver has measured it. */
const useContentSize = (element: HTMLElement | null): ContentSize | null => {
  const [size, setSize] = useState<ContentSize | null>(null)
  useEffect(() => {
    if (!element || typeof ResizeObserver !== "function") {
      setSize(null)
      return
    }
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (!rect) return
      const width = Math.round(rect.width)
      const height = Math.round(rect.height)
      setSize((previous) => {
        if (width <= 0 || height <= 0) return null
        if (previous && previous.width === width && previous.height === height) return previous
        return { width, height }
      })
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])
  return size
}

/**
 * Participant media layout for the custom surface: featured content, then screen share, then a stabilised
 * active-speaker stage with a thumbnail rail, or an equal grid. Tile rendering and video
 * subscriptions are capped for large calls.
 */
export function Stage({ viewMode, layoutMode, stageVideoRef, pinnedId = null, onTogglePin, onActiveSpeakerChange, featured, onStopScreenShare }: StageProps) {
  const localSessionId = useLocalSessionId()
  const participantIds = useParticipantIds({ sort: "joined_at" })
  const { screens } = useScreenShare()
  const activeSpeakerId = useActiveSpeakerHysteresis()

  useEffect(() => {
    onActiveSpeakerChange?.(activeSpeakerId)
  }, [activeSpeakerId, onActiveSpeakerChange])

  const screenShare = screens[0] || null
  const screenShareLabel = useScreenSharerLabel(screenShare?.session_id || "", Boolean(screenShare?.local))
  const screenSharerIds = useMemo(() => screens.map((screen) => screen.session_id), [screens])
  const remoteIds = useMemo(() => participantIds.filter((id) => id !== localSessionId), [localSessionId, participantIds])
  const speakerStageId = activeSpeakerId && participantIds.includes(activeSpeakerId)
    ? activeSpeakerId
    : remoteIds[0] || localSessionId || null
  const pinnedStageId = pinnedId && participantIds.includes(pinnedId) ? pinnedId : null
  const showFeatured = Boolean(featured)
  const stageId = screenShare || showFeatured ? null : pinnedStageId || speakerStageId

  const { visibleIds, hiddenCount } = useTileSubscriptionCap({
    participantIds,
    localSessionId,
    activeSpeakerId,
    screenSharerIds,
    stageId,
    pinnedId: pinnedStageId,
  })

  const isPortrait = layoutMode === "mobile-portrait"
  const [stageElement, setStageElement] = useState<HTMLDivElement | null>(null)
  const stageSize = useContentSize(stageElement)
  const stacked = isPortrait || shouldStackRail(stageSize)
  const useGrid = !screenShare && !showFeatured && !pinnedStageId && (viewMode === "grid" || visibleIds.length <= 2)
  const pinProps = (id: string) => ({
    isPinned: id === pinnedStageId,
    onTogglePin: onTogglePin ? () => onTogglePin(id) : undefined,
  })
  const railIds = screenShare || showFeatured ? visibleIds : visibleIds.filter((id) => id !== stageId)
  const railItems: Array<string | null> = hiddenCount > 0 ? [...railIds, null] : railIds
  const [railElement, setRailElement] = useState<HTMLDivElement | null>(null)
  const railSize = useContentSize(railElement)
  const [railPage, setRailPage] = useState(0)
  const railPageSize = getRailPageSize(railItems.length, railSize?.height)
  const railPageCount = Math.max(1, Math.ceil(railItems.length / railPageSize))
  const currentRailPage = Math.min(railPage, railPageCount - 1)
  const pageItems = railItems.slice(currentRailPage * railPageSize, (currentRailPage + 1) * railPageSize)
  const railFit = !stacked && railSize
    ? fitGridTiles(pageItems.length, railSize.width, railSize.height - (railPageCount > 1 ? RAIL_PAGER_HEIGHT : 0), 1, RAIL_GAP)
    : null

  useEffect(() => {
    setRailPage((previous) => Math.min(previous, railPageCount - 1))
  }, [railPageCount])
  const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null)
  const gridSize = useContentSize(gridElement)
  const gridGap = layoutMode === "desktop" || layoutMode === "tablet" ? 12 : 8
  const gridTileCount = visibleIds.length + (hiddenCount > 0 ? 1 : 0)
  const gridFit = gridSize ? fitGridTiles(gridTileCount, gridSize.width, gridSize.height, getMaxGridColumns(layoutMode), gridGap) : null
  const columns = gridFit?.columns ?? getGridColumns(gridTileCount, layoutMode)

  if (useGrid) {
    return (
      <div
        ref={setGridElement}
        data-testid="call-stage"
        data-stage-layout="grid"
        className="grid h-full w-full content-center justify-center p-2 sm:p-3"
        style={{
          gap: gridGap,
          gridTemplateColumns: gridFit ? `repeat(${columns}, ${gridFit.tileWidth}px)` : `repeat(${columns}, minmax(0, 1fr))`,
        }}
      >
        {visibleIds.map((id, index) => (
          <VideoTile
            key={id}
            ref={index === 0 ? stageVideoRef : undefined}
            sessionId={id}
            variant="grid"
            isLocal={id === localSessionId}
            isActiveSpeaker={id === activeSpeakerId && id !== localSessionId}
            {...pinProps(id)}
          />
        ))}
        {hiddenCount > 0 && (
          <div className="flex aspect-video items-center justify-center rounded-2xl bg-[#1c2530] text-sm font-medium text-white/70 ring-1 ring-white/5">
            +{hiddenCount} more
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      ref={setStageElement}
      data-testid="call-stage"
      data-stage-layout={showFeatured ? "featured" : screenShare ? "screen-share" : "speaker"}
      data-rail-placement={stacked ? "bottom" : "side"}
      className={`flex h-full w-full gap-2 p-2 sm:gap-3 sm:p-3 ${stacked ? "flex-col" : "flex-row"}`}
    >
      <div className="relative min-h-0 min-w-0 flex-1">
        {featured && (
          <div hidden={!showFeatured} data-testid="call-stage-featured" className="h-full w-full overflow-hidden rounded-2xl bg-[#111827] ring-1 ring-white/5">
            {featured}
          </div>
        )}
        {!showFeatured && screenShare?.local ? (
          <div data-testid="call-local-share-card" className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-2xl bg-[#1c2530] p-6 text-center ring-1 ring-white/5">
            <MonitorUp className="h-10 w-10 text-[#f6a35c]" aria-hidden="true" />
            <p className="text-lg font-semibold text-white">You&apos;re sharing your screen</p>
            <p className="max-w-sm text-sm text-white/60">Everyone else sees what you share. It is not shown back to you, so a shared screen never mirrors itself.</p>
            {onStopScreenShare && (
              <button type="button" onClick={onStopScreenShare} data-testid="call-local-share-stop" className="mt-1 rounded-full bg-[#d64545] px-4 py-2 text-sm font-semibold text-white hover:bg-[#c03a3a]">
                Stop sharing
              </button>
            )}
          </div>
        ) : !showFeatured && screenShare ? (
          <div className="h-full w-full overflow-hidden rounded-2xl bg-black ring-1 ring-white/5">
            <CallVideo
              ref={stageVideoRef}
              sessionId={screenShare.session_id}
              type="screenVideo"
              fit="contain"
              className="h-full w-full"
            />
            <span data-testid="call-screen-share-label" className="absolute left-3 top-3 flex max-w-[calc(100%-1.5rem)] items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white">
              <MonitorUp aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{screenShareLabel}</span>
              {screenShareHasAudio(screenShare) && (
                <span className="flex items-center gap-1 text-white/85">
                  <Volume2 aria-hidden="true" className="h-3 w-3" />
                  with audio
                </span>
              )}
            </span>
          </div>
        ) : !showFeatured && stageId ? (
          <VideoTile
            ref={stageVideoRef}
            sessionId={stageId}
            variant="stage"
            isLocal={stageId === localSessionId}
            isActiveSpeaker={stageId === activeSpeakerId && stageId !== localSessionId}
            {...pinProps(stageId)}
          />
        ) : null}
      </div>
      {railIds.length > 0 && (
        <div
          ref={setRailElement}
          data-testid="call-thumbnail-rail"
          data-rail-align={stacked ? "center" : undefined}
          className={`min-h-0 shrink-0 ${stacked ? "h-24 w-full overflow-x-auto overflow-y-hidden" : "flex w-40 flex-col gap-2 overflow-hidden sm:w-48 lg:w-56"}`}
        >
          {stacked ? (
            <div data-testid="call-thumbnail-strip" className="mx-auto flex h-full w-max min-w-0 max-w-none gap-2">
              {railIds.map((id) => (
                <div key={id} className="h-full w-36 shrink-0">
                  <VideoTile sessionId={id} variant="thumb" isLocal={id === localSessionId} isActiveSpeaker={id === activeSpeakerId && id !== localSessionId} className="h-full" {...pinProps(id)} />
                </div>
              ))}
              {hiddenCount > 0 && (
                <div className="flex h-full w-24 shrink-0 items-center justify-center rounded-xl bg-[#1c2530] text-xs font-medium text-white/70 ring-1 ring-white/5">
                  +{hiddenCount} more
                </div>
              )}
            </div>
          ) : (
            <>
              <div data-testid="call-thumbnail-page" className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2">
                {pageItems.map((id) => (
                  <div key={id || "hidden"} data-testid={`call-thumbnail-cell-${id || "hidden"}`} className="min-h-0 w-full shrink" style={railFit ? { width: railFit.tileWidth } : undefined}>
                    {id ? (
                      <VideoTile sessionId={id} variant="thumb" isLocal={id === localSessionId} isActiveSpeaker={id === activeSpeakerId && id !== localSessionId} {...pinProps(id)} />
                    ) : (
                      <div className="flex aspect-video w-full items-center justify-center rounded-xl bg-[#1c2530] text-xs font-medium text-white/70 ring-1 ring-white/5">
                        +{hiddenCount} more
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {railPageCount > 1 && (
                <div className="flex h-8 shrink-0 items-center justify-center gap-2 text-xs text-white/70">
                  <button type="button" aria-label="Previous participants" disabled={currentRailPage === 0} onClick={() => setRailPage(currentRailPage - 1)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30">
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <span role="status" aria-label="Participant page">{currentRailPage + 1} / {railPageCount}</span>
                  <button type="button" aria-label="Next participants" disabled={currentRailPage === railPageCount - 1} onClick={() => setRailPage(currentRailPage + 1)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30">
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
